import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '@afosto/app-components-react/Form';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import AdjustmentsDrawer from './AdjustmentsDrawer';
import ProjectionList from '../ProjectionList';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import uuid from '@afosto/utils/uuid';
import styled from 'styled-components';
import translations from './translations';

const TotalsRow = styled.div`
  display: flex;
  padding-bottom: ${props => props.theme.spacing()}px;
  padding-top: ${props => props.theme.spacing()}px;
`;

const TotalsRowDescription = styled(Typography)`
  && {
    flex: 1;
  }
`;

const TotalsFormSection = ({ projection, isFetchingProjection, allowAddDiscount }) => {
  const intl = useIntl();
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const formContext = useFormContext();
  const { setValue, watch } = formContext || {};
  const adjustmentsValue = watch('adjustments');
  const formattedAdjustmentsValue = (adjustmentsValue ?? []).reduce(
    (acc, adjustment) => [
      ...acc,
      {
        id: adjustment?.reference ?? uuid(),
        description: adjustment?.description,
        isDiscount: adjustment?.is_discount ?? true,
        amount: adjustment?.amount,
        type: adjustment?.is_percentage ? 'percentage' : 'amount',
      },
    ],
    [],
  );

  const handleSaveAdjustments = async ({ adjustments = [] }) => {
    const formattedAdjustments = adjustments.reduce((acc, adjustment) => {
      const isPercentage = adjustment?.type === 'percentage';
      const amount = adjustment?.amount ?? 0;

      return [
        ...acc,
        {
          reference: adjustment?.id,
          description: adjustment?.description,
          amount: !isPercentage ? parseInt(amount, 10) : amount,
          is_percentage: isPercentage,
          is_discount: adjustment?.isDiscount,
        },
      ];
    }, []);

    setValue('adjustments', formattedAdjustments);
    setShowAddDiscount(false);
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            allowAddDiscount && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => setShowAddDiscount(true)}
                disabled={!projection || projection.items.length === 0}
              >
                {intl.formatMessage(translations.button.discounts.label)}
              </Button>
            )
          }
        />

        {isFetchingProjection && (
          <CardContent>
            <TotalsRow>
              <TotalsRowDescription component="div">
                <SkeletonBody lines={1} />
              </TotalsRowDescription>
            </TotalsRow>
            <TotalsRow>
              <TotalsRowDescription component="div">
                <SkeletonBody lines={1} />
              </TotalsRowDescription>
            </TotalsRow>
            <TotalsRow>
              <TotalsRowDescription component="div">
                <SkeletonBody lines={1} />
              </TotalsRowDescription>
            </TotalsRow>
          </CardContent>
        )}

        {!isFetchingProjection && (
          <CardContent>
            <ProjectionList projection={projection} />
          </CardContent>
        )}
      </Card>
      <AdjustmentsDrawer
        adjustments={formattedAdjustmentsValue}
        isOpen={showAddDiscount}
        onClose={() => setShowAddDiscount(false)}
        onSave={handleSaveAdjustments}
      />
    </>
  );
};

TotalsFormSection.propTypes = {
  allowAddDiscount: PropTypes.bool,
  projection: PropTypes.object,
  isFetchingProjection: PropTypes.bool,
};

TotalsFormSection.defaultProps = {
  allowAddDiscount: true,
  projection: null,
  isFetchingProjection: true,
};

export default TotalsFormSection;
